import { Sparkles, Radar, ClipboardCheck, Globe, type LucideIcon } from "lucide-react";

interface Benefit {
  icon: LucideIcon;
  title: string;
  body: string;
}

const BENEFITS: Benefit[] = [
  {
    icon: Radar,
    title: "AI does the discovery",
    body: "Autonomously surfaces, classifies and scores every candidate location across a whole city — no manual map-scouring or desk research.",
  },
  {
    icon: ClipboardCheck,
    title: "People only validate",
    body: "Humans spend their time reviewing, checking sites on the ground and getting city sign-off — the judgment calls, not the grunt work.",
  },
  {
    icon: Globe,
    title: "Repeatable at any scale",
    body: "The same pipeline runs identically for one neighbourhood or an entire country — consistent, fast and ready to expand.",
  },
];

/**
 * "Why it matters" band shown directly under the three-phase hero diagram.
 * Frames the whole flow before the detailed timeline: the proprietary AI
 * automates the slow, manual part; people are reserved for verification.
 */
export function AutomationSummary() {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-violet-200/70 bg-gradient-to-br from-violet-50 to-transparent p-6 md:p-8 dark:border-violet-800/50 dark:from-violet-950/25">
      {/* soft accent glow */}
      <div className="pointer-events-none absolute -right-12 -top-12 size-44 rounded-full bg-violet-400/15 blur-3xl" />

      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
          <Sparkles className="size-3.5" />
          The Equip Scout advantage
        </span>

        <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          We automate the hard part with AI
        </h2>

        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground md:text-base">
          Finding and vetting every candidate location is the slow, manual grind — weeks
          of scouring maps and cross-checking the web. Equip Scout&apos;s proprietary AI
          does it autonomously, so the people involved only spend their time on the
          decisions that genuinely need a human.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {BENEFITS.map((benefit) => {
            const Icon = benefit.icon;
            return (
              <div
                key={benefit.title}
                className="rounded-xl border border-border bg-card/70 p-4 backdrop-blur-sm"
              >
                <span className="inline-flex size-9 items-center justify-center rounded-lg bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300">
                  <Icon className="size-4" />
                </span>
                <h3 className="mt-3 text-sm font-semibold text-foreground">
                  {benefit.title}
                </h3>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {benefit.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
